import React, { createContext, useContext, useState, useEffect } from 'react';

interface Language {
  code: string;
  name: string;
  flag: string;
}

interface LanguageContextType {
  currentLanguage: Language;
  setCurrentLanguage: (language: Language) => void;
  availableLanguages: Language[];
}

const languages: Language[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
];

// Default to English (first language in array)
const defaultLanguage = languages[0];

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(defaultLanguage);

  // Initialize language preference
  useEffect(() => {
    const initializeLanguage = () => {
      try {
        // First, try to get saved language preference
        const savedLanguage = localStorage.getItem('manivo_language');
        if (savedLanguage) {
          const language = languages.find(lang => lang.code === savedLanguage);
          if (language) {
            setCurrentLanguage(language);
            return;
          }
        }

        // Second, try to detect browser language
        if (navigator.language) {
          const browserLang = navigator.language.split('-')[0].toLowerCase();
          const detectedLanguage = languages.find(lang => lang.code === browserLang);
          if (detectedLanguage) {
            setCurrentLanguage(detectedLanguage);
            return;
          }
        }

        // Fallback to English (already set as default)
        setCurrentLanguage(defaultLanguage);
      } catch (error) {
        console.warn('Language initialization error, defaulting to English:', error);
        setCurrentLanguage(defaultLanguage);
      }
    };

    initializeLanguage();
  }, []);

  // Save language preference when changed
  const handleLanguageChange = (language: Language) => {
    try {
      setCurrentLanguage(language);
      localStorage.setItem('manivo_language', language.code);
      
      // Set document language for accessibility
      document.documentElement.lang = language.code;
      
      console.log(`Language changed to: ${language.name} (${language.code})`);
    } catch (error) {
      console.error('Error saving language preference:', error);
      // Still update the state even if saving fails
      setCurrentLanguage(language);
    }
  };

  // Set initial document language
  useEffect(() => {
    document.documentElement.lang = currentLanguage.code;
  }, [currentLanguage]);

  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      setCurrentLanguage: handleLanguageChange,
      availableLanguages: languages
    }}>
      {children}
    </LanguageContext.Provider>
  );
}; 